import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { SortingOrderE } from 'declarations/enums/common';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  AssigneeFilterValuesE,
  ParameterSortingValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import { NoopT } from 'declarations/types/common';
import { SelectOptionT } from 'declarations/types/select';

type UseTicketsRefinementT = {
  isOpenStatusFilterActive: boolean;
  isCloseStatusFilterActive: boolean;
  isUnder24HoursFilterActive: boolean;
  isOver24HoursFilterActive: boolean;
  isAssignedToMeFilterActive: boolean;
  isPastMonthFilterActive: boolean;
  isPast30DaysFilterActive: boolean;
  isArchivedFilterActive: boolean;
  isFiltersPopupOpened: boolean;
  isRONumberSortingActive: boolean;
  isLineNumberSortingActive: boolean;
  isAgeSortingActive: boolean;
  isClaimAgeSortingActive: boolean;
  isCloseROAgeSortingActive: boolean;
  isSubCategorySortingActive: boolean;
  isStatusSortingActive: boolean;
  isAssigneeSortingActive: boolean;
  isCategorySortingActive: boolean;
  isAscSortingActive: boolean;
  isDescSortingActive: boolean;
  isAmountSortingActive: boolean;
  isTechSortingActive: boolean;
  isAdvisorSortingActive: boolean;
  isApprovedByDealerSortingActive: boolean;
  hasAttachmentSortingActive: boolean;
  isClaimStatusSortingActive: boolean;
  isTicketPrioritySortingActive: boolean;
  isAssignToDealerFilterActive: boolean;
  isAssignToWarrCloudFilterActive: boolean;
  isAssignToOEMFilterActive: boolean;
  sortOptions: SelectOptionT[];
  sortSelectValue: string;
  handleUpdateFilterClick: (key: TicketsRefinementKeysE, value: string) => NoopT;
  handleUpdateSortingClick: (key: TicketsRefinementKeysE, value: string) => NoopT;
  handleOrderSortSelectClick: (key: TicketsRefinementKeysE, value: SortingOrderE) => NoopT;
  handleUpdateSortSelectClick: (value: any) => void;
  handleClearAllFiltersClick: NoopT;
  handleOpenFiltersPopup: NoopT;
  handleCloseFiltersPopup: NoopT;
  selectKey: number;
  filtersReset: number;
  handleUpdateScheduleSelectClick: (value: any) => void;
  handleScheduleAgeFilterChange: (event: string | null) => void;
  scheduleAgeOptions: SelectOptionT[];
  selectedAgeFilter: string;
};

const useTicketsRefinement = (): UseTicketsRefinementT => {
  /**
   * Hooks
   */
  const {
    getSearchParam,
    updateSearchParam,
    clearSearchParam,
    updateSearchParamsArray,
    setActiveScheduleCards,
    setActiveScheduleFilters,
    activeTab,
    scheduleCategoryId,
  } = useTickets();
  const [isFiltersPopupOpened, setFiltersPopupVisibility] = useState(false);
  const [sortSelectValue, setSortSelectValue] = useState('');
  const [filtersReset, setFiltersReset] = useState(0);
  const [selectKey, setSelectKey] = useState(0);
  const [selectedAgeFilter, setSelectedAgeFilter] = useState('');

  /**
   * Variables
   */
  const statusFilter = getSearchParam(TicketsRefinementKeysE.Status);
  const pastMonthFilter = getSearchParam(TicketsRefinementKeysE.PastMonth);
  const past30DaysFilter = getSearchParam(TicketsRefinementKeysE.Past30Days);
  const archivedFilter = getSearchParam(TicketsRefinementKeysE.IsArchived);
  const ageFilter = getSearchParam(TicketsRefinementKeysE.TicketAge);
  const assignedToMeFilter = getSearchParam(TicketsRefinementKeysE.AssignedToMe);
  const assigneeFilter = getSearchParam(TicketsRefinementKeysE.Assignee);
  const sortingParameter = getSearchParam(TicketsRefinementKeysE.Parameter);
  const sortingOrder = getSearchParam(TicketsRefinementKeysE.Order);
  const isOpenStatusFilterActive = !!statusFilter && statusFilter === StatusFilterValuesE.Open;
  const isCloseStatusFilterActive = !!statusFilter && statusFilter === StatusFilterValuesE.Closed;
  const isPastMonthFilterActive = !!pastMonthFilter && pastMonthFilter === 'true';
  const isPast30DaysFilterActive = !!past30DaysFilter && past30DaysFilter === 'true';
  const isArchivedFilterActive = !!archivedFilter && archivedFilter === 'true';
  const isUnder24HoursFilterActive = !!ageFilter && ageFilter === AgeFilterValuesE.Under24Hours;
  const isOver24HoursFilterActive = !!ageFilter && ageFilter === AgeFilterValuesE.Over24Hours;
  const isAssignedToMeFilterActive = !!assignedToMeFilter && assignedToMeFilter === 'true';
  const isRONumberSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.RONumber;
  const isLineNumberSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.LineNumber;
  const isAgeSortingActive = !!sortingParameter && sortingParameter === ParameterSortingValuesE.Age;
  const isClaimAgeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClaimAge;
  const isCloseROAgeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClosedROAge;
  const isStatusSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Status;
  const isAssigneeSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Assignee;
  const isCategorySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.RequestCategory;
  const isSubCategorySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.SubCategory;
  const isAmountSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Amount;
  const isApprovedByDealerSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.IsApprovedByDealer;
  const hasAttachmentSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.HasAttachment;
  const isClaimStatusSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.ClaimStatus;
  const isTicketPrioritySortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.TicketPriority;
  const isAdvisorSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Advisor;
  const isTechSortingActive =
    !!sortingParameter && sortingParameter === ParameterSortingValuesE.Tech;
  const isAscSortingActive = !!sortingOrder && sortingOrder === SortingOrderE.Asc;
  const isDescSortingActive = !!sortingOrder && sortingOrder === SortingOrderE.Desc;
  const isAssignToDealerFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.Dealer;
  const isAssignToWarrCloudFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.WarrCloud;
  const isAssignToOEMFilterActive =
    !!assigneeFilter && assigneeFilter === AssigneeFilterValuesE.OEM;

  const sortOptions = useMemo(
    () => [
      {
        label: 'Ticket Age',
        value: `${ParameterSortingValuesE.Age}`,
      },
      {
        label: 'Closed RO Age',
        value: `${ParameterSortingValuesE.ClosedROAge}`,
      },
      {
        label: 'RO number',
        value: `${ParameterSortingValuesE.RONumber}`,
      },
      {
        label: 'Ticket number',
        value: `${ParameterSortingValuesE.LineNumber}`,
      },
      {
        label: 'Ticket Status',
        value: `${ParameterSortingValuesE.Status}`,
      },
      {
        label: 'Ticket Assignee',
        value: `${ParameterSortingValuesE.Assignee}`,
      },
      {
        label: 'Request Category',
        value: `${ParameterSortingValuesE.RequestCategory}`,
      },
      {
        label: 'Ticket Amount',
        value: `${ParameterSortingValuesE.Amount}`,
      },
      {
        label: 'Claim Status',
        value: `${ParameterSortingValuesE.ClaimStatus}`,
      },
      {
        label: 'Tech Number',
        value: `${ParameterSortingValuesE.Tech}`,
      },
      {
        label: 'Advisor Number',
        value: `${ParameterSortingValuesE.Advisor}`,
      },
    ],
    []
  );
  const scheduleAgeOptions = useMemo(
    () => [
      {
        label: '-',
        value: `${ScheduleFilterValuesE.Undefined}`,
      },
      {
        label: '0-30 days',
        value: `${ScheduleFilterValuesE.LessThan31Days}`,
      },
      {
        label: '31-60 days',
        value: `${ScheduleFilterValuesE.From31To60Days}`,
      },
      {
        label: '61-90 days',
        value: `${ScheduleFilterValuesE.From61To90Days}`,
      },
      {
        label: '91-120 days',
        value: `${ScheduleFilterValuesE.From91To120Days}`,
      },
      {
        label: '121+',
        value: `${ScheduleFilterValuesE.Over120Days}`,
      },
    ],
    []
  );
  /**
   * Handlers
   */
  const handleUpdateFilterClick = useCallback(
    (key: TicketsRefinementKeysE, value: string) => () => {
      const filterValue = getSearchParam(key);
      const isFilterSelected = filterValue && filterValue === value;
      const isArchivedFilterClicked = key === TicketsRefinementKeysE.IsArchived;
      const isPastMonthFilterActive = getSearchParam(TicketsRefinementKeysE.PastMonth) === 'true';

      if (
        isPastMonthFilterActive &&
        (key === TicketsRefinementKeysE.TicketAge || key === TicketsRefinementKeysE.Past30Days)
      )
        clearSearchParam(TicketsRefinementKeysE.PastMonth);

      if (isArchivedFilterClicked) {
        clearSearchParam([
          TicketsRefinementKeysE.AssignedToMe,
          TicketsRefinementKeysE.Status,
          TicketsRefinementKeysE.TicketAge,
          TicketsRefinementKeysE.PastMonth,
          TicketsRefinementKeysE.Past30Days,
        ]);
      } else if (!isFilterSelected) {
        clearSearchParam(TicketsRefinementKeysE.IsArchived);
      }

      if (isFilterSelected) {
        clearSearchParam(key);
      } else {
        updateSearchParam(key, value);
      }
    },
    [clearSearchParam, updateSearchParam, getSearchParam]
  );
  const handleUpdateSortingClick = useCallback(
    (key: TicketsRefinementKeysE, value: string) => () => {
      const sortValue = getSearchParam(TicketsRefinementKeysE.Order);

      updateSearchParam(key, value);

      if (sortValue === SortingOrderE.Asc) {
        updateSearchParam(TicketsRefinementKeysE.Order, SortingOrderE.Desc);
      } else {
        updateSearchParam(TicketsRefinementKeysE.Order, SortingOrderE.Asc);
      }
    },
    [updateSearchParam, getSearchParam]
  );
  const handleUpdateSortSelectClick = useCallback(
    (value: string) => {
      updateSearchParam(TicketsRefinementKeysE.Parameter, value);
      setSortSelectValue(value);
    },
    [updateSearchParam]
  );
  const handleOrderSortSelectClick = useCallback(
    (key: TicketsRefinementKeysE, value: SortingOrderE) => () => {
      updateSearchParam(key, value);
    },
    [updateSearchParam]
  );

  const handleClearAllFiltersClick = () => {
    setSortSelectValue('');
    setSelectedAgeFilter(ScheduleFilterValuesE.Undefined);
    setSelectKey(prevKey => prevKey + 1);
    const shouldPreserveSchedule = activeTab === ActiveTabTypeE.Schedule;
    clearSearchParam([
      TicketsRefinementKeysE.RoofTopId,
      TicketsRefinementKeysE.RONumber,
      TicketsRefinementKeysE.SearchTerm,
      TicketsRefinementKeysE.Status,
      TicketsRefinementKeysE.TicketAge,
      TicketsRefinementKeysE.AssignedToMe,
      TicketsRefinementKeysE.Parameter,
      TicketsRefinementKeysE.Order,
      TicketsRefinementKeysE.PastMonth,
      TicketsRefinementKeysE.Past30Days,
      TicketsRefinementKeysE.IsArchived,
      TicketsRefinementKeysE.Schedule,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.Assignee,
      ...(shouldPreserveSchedule ? [] : [TicketsRefinementKeysE.Category]),
    ]);
    if (shouldPreserveSchedule && scheduleCategoryId) {
      updateSearchParamsArray(TicketsRefinementKeysE.Category, [scheduleCategoryId]);
    }
    setFiltersReset(prev => prev + 1);
    setActiveScheduleCards([]);
    setActiveScheduleFilters([]);
  };

  const handleOpenFiltersPopup = () => {
    setFiltersPopupVisibility(true);
  };
  const handleCloseFiltersPopup = () => {
    setFiltersPopupVisibility(false);
  };
  const handleUpdateScheduleSelectClick = useCallback(
    (value: string) => {
      updateSearchParam(TicketsRefinementKeysE.Schedule, value.toString());
      setSelectedAgeFilter(value.toString());
    },
    [updateSearchParam]
  );

  const handleScheduleAgeFilterChange = useCallback(
    (option: string | null) => {
      if (option) {
        setSelectedAgeFilter(option.toString());
        updateSearchParam(TicketsRefinementKeysE.Schedule, option.toString());
      }
    },
    [updateSearchParam]
  );

  /**
   * Effects
   */
  useEffect(() => {
    const scheduleAgeFromQuery = getSearchParam(TicketsRefinementKeysE.Schedule);
    if (scheduleAgeFromQuery) {
      setSelectedAgeFilter(scheduleAgeFromQuery);
    }
  }, [getSearchParam]);

  return {
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isSubCategorySortingActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
    isFiltersPopupOpened,
    isRONumberSortingActive,
    isLineNumberSortingActive,
    isAscSortingActive,
    isStatusSortingActive,
    isCloseROAgeSortingActive,
    isDescSortingActive,
    isAssigneeSortingActive,
    isCategorySortingActive,
    isAgeSortingActive,
    isClaimAgeSortingActive,
    isAmountSortingActive,
    isAdvisorSortingActive,
    isTechSortingActive,
    isApprovedByDealerSortingActive,
    hasAttachmentSortingActive,
    isClaimStatusSortingActive,
    isTicketPrioritySortingActive,
    isAssignToDealerFilterActive,
    isAssignToWarrCloudFilterActive,
    isAssignToOEMFilterActive,
    sortOptions,
    sortSelectValue,
    handleUpdateFilterClick,
    handleUpdateSortingClick,
    handleUpdateSortSelectClick,
    handleOrderSortSelectClick,
    handleClearAllFiltersClick,
    handleOpenFiltersPopup,
    handleCloseFiltersPopup,
    scheduleAgeOptions,
    handleScheduleAgeFilterChange,
    handleUpdateScheduleSelectClick,
    selectKey,
    filtersReset,
    selectedAgeFilter,
  };
};

export default useTicketsRefinement;
